import { Box, Typography } from "@mui/material";
import { values } from "../data/data.js";
import { useState } from "react";

const SDG_COLORS = ["#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21","#26BDE2","#FCC30B","#A21942","#FD6925","#DD1367","#FD9D24","#BF8B2E","#3F7E44","#0A97D9","#56C02B","#00689D","#19486A"];

export default function Values() {
  const [activeIndex, setActiveIndex] = useState(null);

  const hero   = values[0];
  const others = values.slice(1);          // exactly 4 remaining cards

  return (
    <Box id="values" sx={{ bgcolor: "#F7F4EF", position: "relative", overflow: "hidden" }}>

      {/* grain */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.5,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
        backgroundSize: "128px",
      }} />

      {/* ── Top label bar ── */}
      <Box sx={{
        borderBottom: "1px solid rgba(26,10,0,0.12)",
        px: { xs: 3, md: 6 }, py: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "relative", zIndex: 1,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: "50%", bgcolor: "#5C3311",
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%,100%": { opacity: 1, transform: "scale(1)" },
              "50%":      { opacity: 0.4, transform: "scale(0.8)" },
            },
          }} />
          <Typography sx={{ fontFamily: "'Space Mono',monospace", fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "rgba(26,10,0,0.45)" }}>
            Our Core Values
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: "'Space Mono',monospace", fontSize: "10px", letterSpacing: "2px", color: "rgba(26,10,0,0.30)" }}>
          {String(values.length).padStart(2,"0")} Principles
        </Typography>
      </Box>

      {/* ── SECTION HEADER ── */}
      <Box sx={{
        px: { xs: 3, md: 6 }, pt: { xs: 5, md: 6 }, pb: { xs: 4, md: 5 },
        display: "flex", flexDirection: { xs: "column", md: "row" },
        alignItems: { md: "flex-end" }, justifyContent: "space-between", gap: 3,
        position: "relative", zIndex: 1,
        borderBottom: "1px solid rgba(26,10,0,0.10)",
      }}>
        <Box>
          <Typography sx={{
            fontFamily: "'Space Mono',monospace", fontSize: "10px",
            letterSpacing: "3px", textTransform: "uppercase", color: "#5C3311",
            mb: 2, fontWeight: 700,
          }}>
            → What We Stand For
          </Typography>
          <Typography sx={{
            fontFamily: "'Playfair Display',serif",
            fontSize: { xs: "36px", md: "52px", lg: "60px" },
            fontWeight: 700, color: "#1a0a00",
            lineHeight: 1.05, letterSpacing: "-1.5px",
          }}>
            The values that
            <br />
            drive{" "}
            <Box component="span" sx={{
              position: "relative",
              "&::after": {
                content: '""', position: "absolute",
                bottom: 6, left: 0, width: "100%", height: "3px",
                background: "linear-gradient(90deg,#E5243B,#DDA63A,#4C9F38)",
                borderRadius: "2px",
              },
            }}>
              every build.
            </Box>
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 320 }}>
          <Typography sx={{ fontSize: "14px", color: "rgba(26,10,0,0.52)", lineHeight: 1.78, mb: 3 }}>
            From the foundation up, these principles guide how we design, build,
            and serve communities across Africa.
          </Typography>
          {/* SDG strip */}
          <Box sx={{ display: "flex", height: "3px", borderRadius: "2px", overflow: "hidden" }}>
            {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════
          BENTO GRID  —  hero left · 2×2 right
      ══════════════════════════════════════════ */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1.15fr 1fr 1fr" },
        gridTemplateRows:    { lg: "repeat(2, 380px)" },
        position: "relative", zIndex: 1,
        borderTop: "none",
      }}>

        {/* ── HERO card (col 1, spans 2 rows) ── */}
        <Box
          onMouseEnter={() => setActiveIndex(0)}
          onMouseLeave={() => setActiveIndex(null)}
          sx={{
            gridColumn: { xs: "1", md: "1", lg: "1" },
            gridRow:    { lg: "1 / 3" },
            position: "relative", overflow: "hidden",
            minHeight: { xs: "360px", md: "420px", lg: "unset" },
            borderRight:  { lg: "1px solid rgba(26,10,0,0.10)" },
            borderBottom: { xs: "1px solid rgba(26,10,0,0.10)", lg: "none" },
            filter: activeIndex !== null && activeIndex !== 0
              ? "brightness(0.70) saturate(0.65)" : "brightness(1) saturate(1)",
            transition: "filter 0.45s ease",
            "&:hover .vi": { transform: "scale(1.06)" },
            "&:hover .vo": { background: "linear-gradient(to top,rgba(6,2,0,0.96) 0%,rgba(6,2,0,0.55) 55%,rgba(6,2,0,0.12) 100%)" },
            "&:hover .vb": { opacity: 1, transform: "translateY(0)" },
            "&:hover .vr": { width: "52px" },
            "&:hover .vt": { opacity: 1, transform: "translateY(0)" },
          }}
        >
          <Box component="img" className="vi" src={hero.image} alt={hero.name}
            sx={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover",
                  transition:"transform 0.85s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
          <Box className="vo" sx={{
            position:"absolute", inset:0,
            background:"linear-gradient(to top,rgba(6,2,0,0.90) 0%,rgba(6,2,0,0.38) 52%,rgba(6,2,0,0.06) 100%)",
            transition:"background 0.5s ease",
          }} />
          {/* accent bar */}
          <Box sx={{ position:"absolute", top:0, left:0, right:0, height:"4px", bgcolor: hero.color||"#DDA63A", zIndex:3 }} />
          {/* number */}
          <Typography sx={{
            position:"absolute", top:20, left:22,
            fontFamily:"'Space Mono',monospace", fontSize:"11px", fontWeight:700,
            color:"rgba(255,255,255,0.45)", letterSpacing:"2px", zIndex:2,
          }}>01</Typography>
          {/* hover tag */}
          <Box className="vt" sx={{
            position:"absolute", top:14, right:14,
            bgcolor: hero.color||"#DDA63A", color:"#fff",
            px:1.5, py:0.4, borderRadius:"3px", zIndex:4,
            opacity:0, transform:"translateY(-6px)",
            transition:"opacity 0.35s ease, transform 0.35s ease",
          }}>
            <Typography sx={{ fontFamily:"'Space Mono',monospace", fontSize:"9px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase" }}>
              01 / {String(values.length).padStart(2,"0")}
            </Typography>
          </Box>
          {/* text */}
          <Box sx={{ position:"absolute", bottom:0, left:0, right:0, p:{ xs:3, md:4 }, zIndex:3 }}>
            <Typography sx={{
              fontFamily:"'Playfair Display',serif",
              fontSize:{ xs:"28px", md:"34px", lg:"38px" },
              fontWeight:700, color:"#fff", lineHeight:1.1, mb:1.5,
            }}>{hero.name}</Typography>
            <Box className="vr" sx={{ width:"20px", height:"2.5px", bgcolor:hero.color||"#DDA63A", borderRadius:"1px", mb:2, transition:"width 0.45s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
            <Typography className="vb" sx={{
              fontSize:"14px", color:"rgba(255,255,255,0.82)", lineHeight:1.78,
              opacity:0, transform:"translateY(10px)",
              transition:"opacity 0.45s ease 0.05s, transform 0.45s ease 0.05s",
              maxWidth:"420px",
            }}>{hero.desc}</Typography>
          </Box>
        </Box>

        {/* ── 4 smaller cards in 2×2 ── */}
        {others.map((v, idx) => {
          const i = idx + 1;
          return (
            <Box
              key={v.name}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              sx={{
                position:"relative", overflow:"hidden",
                minHeight:{ xs:"280px", md:"300px", lg:"unset" },
                borderLeft:  { lg: idx % 2 === 0 ? "none" : "1px solid rgba(26,10,0,0.10)" },
                borderBottom: { xs:"1px solid rgba(26,10,0,0.10)", lg: idx < 2 ? "1px solid rgba(26,10,0,0.10)" : "none" },
                filter: activeIndex !== null && activeIndex !== i
                  ? "brightness(0.70) saturate(0.65)" : "brightness(1) saturate(1)",
                transition:"filter 0.45s ease",
                "&:hover .vi": { transform:"scale(1.08)" },
                "&:hover .vo": { background:"linear-gradient(to top,rgba(6,2,0,0.96) 0%,rgba(6,2,0,0.58) 52%,rgba(6,2,0,0.12) 100%)" },
                "&:hover .vb": { opacity:1, transform:"translateY(0)" },
                "&:hover .vr": { width:"44px" },
                "&:hover .vt": { opacity:1, transform:"translateY(0)" },
              }}
            >
              <Box component="img" className="vi" src={v.image} alt={v.name}
                sx={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover",
                      transition:"transform 0.8s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
              <Box className="vo" sx={{
                position:"absolute", inset:0,
                background:"linear-gradient(to top,rgba(6,2,0,0.88) 0%,rgba(6,2,0,0.32) 52%,rgba(6,2,0,0.04) 100%)",
                transition:"background 0.5s ease",
              }} />
              <Box sx={{ position:"absolute", top:0, left:0, right:0, height:"3px", bgcolor:v.color||"#DDA63A", zIndex:3 }} />
              <Typography sx={{
                position:"absolute", top:16, left:18,
                fontFamily:"'Space Mono',monospace", fontSize:"10px", fontWeight:700,
                color:"rgba(255,255,255,0.40)", letterSpacing:"2px", zIndex:2,
              }}>
                {String(i + 1).padStart(2,"0")}
              </Typography>
              <Box className="vt" sx={{
                position:"absolute", top:12, right:12,
                bgcolor:v.color||"#DDA63A", color:"#fff",
                px:1.2, py:0.3, borderRadius:"3px", zIndex:4,
                opacity:0, transform:"translateY(-6px)",
                transition:"opacity 0.35s ease, transform 0.35s ease",
              }}>
                <Typography sx={{ fontFamily:"'Space Mono',monospace", fontSize:"9px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase" }}>
                  {String(i + 1).padStart(2,"0")} / {String(values.length).padStart(2,"0")}
                </Typography>
              </Box>
              <Box sx={{ position:"absolute", bottom:0, left:0, right:0, p:{ xs:2.5, md:3 }, zIndex:3 }}>
                <Typography sx={{
                  fontFamily:"'Playfair Display',serif",
                  fontSize:{ xs:"20px", md:"22px", lg:"24px" },
                  fontWeight:700, color:"#fff", lineHeight:1.15, mb:1,
                }}>{v.name}</Typography>
                <Box className="vr" sx={{ width:"18px", height:"2px", bgcolor:v.color||"#DDA63A", borderRadius:"1px", mb:1.5, transition:"width 0.45s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
                <Typography className="vb" sx={{
                  fontSize:{ xs:"12px", md:"13px" }, color:"rgba(255,255,255,0.80)", lineHeight:1.72,
                  opacity:0, transform:"translateY(10px)",
                  transition:"opacity 0.45s ease 0.05s, transform 0.45s ease 0.05s",
                }}>{v.desc}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Bottom bar ── */}
      <Box sx={{
        borderTop:"1px solid rgba(26,10,0,0.10)",
        px:{ xs:3, md:6 }, py:2.5,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        flexWrap:"wrap", gap:2, position:"relative", zIndex:1,
      }}>
        <Typography sx={{ fontFamily:"'Space Mono',monospace", fontSize:"10px", color:"rgba(26,10,0,0.35)", letterSpacing:"2px", textTransform:"uppercase" }}>
          Youth-led · Pan-African · Est. 2025
        </Typography>
        <Typography component="a" href="#programs" sx={{
          fontFamily:"'Space Mono',monospace", fontSize:"11px", fontWeight:700,
          color:"#5C3311", textDecoration:"none", letterSpacing:"1.5px", textTransform:"uppercase",
          borderBottom:"1.5px solid #5C3311", pb:"1px",
          transition:"all 0.2s",
          "&:hover":{ color:"#DDA63A", borderColor:"#DDA63A" },
        }}>
          See Our Programs →
        </Typography>
      </Box>
    </Box>
  );
}